import { Col, Container, Nav, NavItem, NavLink, Row, TabContent, TabPane, Button, CardBody } from "reactstrap";
import React, { useEffect, useState } from "react";

import API from "../../utils/API";
import AdminSelect from "../../components/formfields/AdminSelect";
import DateRange from "../../components/formfields/DateRange";
import LenderSelect from "../../components/formfields/LenderSelect";
import Loader from "../../components/Loader";
import ProductMulti from "../../components/formfields/ProductMulti";
import dateFormat from "dateformat";
import { parseFilterValues } from "../../utils/helpers/filterHelper";
import { useHistory } from "react-router-dom";
import classnames from "classnames";
import OrderSummaryByDate from "./OrderSummaryByDate";
import OrderSummaryByOrder from "./OrderSummaryByOrder";

const OrderSummary = () => {
  const history = useHistory();
  const defaultDate =
      dateFormat(new Date().setDate(new Date().getDate() - 6), "mmm d, yyyy") +
      " to " +
      dateFormat(new Date(), "mmm d, yyyy");
  const defaultFiltrationState = {
    date: defaultDate,
  };

  const getParsedFilters = (searchString) => {
    const search = searchString !== undefined ? searchString : history.location.search;
    const urlParams = new URLSearchParams(search);
    const parsedParams = Object.fromEntries(urlParams.entries());
    const filtersParsed = parseFilterValues(parsedParams);
    const tab = urlParams.get("tab") || "summary";
    return { ...defaultFiltrationState, ...filtersParsed, tab };
  };

  const [filtration, setFiltration] = useState(() => getParsedFilters());
  const [appliedFiltration, setAppliedFiltration] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryDataDate, setSummaryDataDate] = useState(null);
  const [byOrderData, setByOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const urlParams = new URLSearchParams(history.location.search);
    return urlParams.get("tab") || "summary";
  });

  const loadResults = (filters) => {
    if (!filters?.date) return;
    setLoading(true);
    const requestFiltration = { date: filters.date, product: filters.product, lender: filters.lender, representative: filters.representative };
    Promise.all([
      API.post("/admin/lender/summaryorder", requestFiltration),
      API.post("/admin/lender/summaryorderbyorder", requestFiltration),
    ])
      .then(([summaryRes, byOrderRes]) => {
        const summaryPayload = summaryRes.data?.data;
        setSummaryData(summaryPayload?.report ?? null);
        setSummaryDataDate(summaryPayload?.reportDate ?? null);
        setByOrderData(byOrderRes.data?.data ?? []);
        setAppliedFiltration(requestFiltration);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const filtersChanged = appliedFiltration !== null &&
    JSON.stringify({
      date: filtration.date,
      product: filtration.product,
      lender: filtration.lender,
      representative: filtration.representative,
    }) !== JSON.stringify(appliedFiltration);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      const next = getParsedFilters(location.search);
      setFiltration(next);
      setActiveTab(next.tab || "summary");
    });
    return () => unlisten();
  }, [history]);

  const pushWithTab = (newFilters, tab = activeTab) => {
    const searchParams = new URLSearchParams();
    const { tab: _t, ...rest } = newFilters;
    Object.entries({ ...rest, tab }).forEach(([key, val]) => {
      if (val === undefined || val === null) return;
      if (Array.isArray(val)) {
        if (val.length) searchParams.set(key, val.map(String).join(","));
      } else {
        searchParams.set(key, String(val));
      }
    });
    history.push({
      pathname: history.location.pathname,
      search: searchParams.toString(),
    });
  };

  const changeData = (name, value) => {
    const newFilters = { ...filtration, [name]: value };
    setFiltration(newFilters);
    pushWithTab(newFilters);
  };

  const changeNativeData = (e) => {
    const newFilters = { ...filtration, [e.target.name]: e.target.value };
    setFiltration(newFilters);
    pushWithTab(newFilters);
  };

  return (
      <Container fluid className="p-0">
        {filtersChanged && (
          <style>{`
            @keyframes show-results-pulse {
              0%, 100% { box-shadow: 0 0 12px 2px rgba(0, 123, 255, 0.5); }
              50% { box-shadow: 0 0 20px 5px rgba(0, 123, 255, 0.85); }
            }
          `}</style>
        )}
        <div className="row justify-content-between">
          <div className="col-auto">
            <h3 className="h3 mb-3 float-left">Orders summary</h3>
          </div>
        </div>
        {loading ? (
          <Loader />
        ) : (
          <>
            <Row form className={"mb-2"}>
              <Col md={3}>
                <DateRange
                    onChange={changeData}
                    name={"date"}
                    value={filtration?.date}
                />
              </Col>
              <Col md={3}>
                <ProductMulti
                    name={"product"}
                    onChange={changeData}
                    value={filtration?.product}
                />
              </Col>
              <Col md={3}>
                <LenderSelect
                    name={"lender"}
                    onChange={changeData}
                    value={filtration?.lender}
                />
              </Col>
              <Col md={3}>
                <AdminSelect
                    name={"representative"}
                    onChange={changeNativeData}
                    required={false}
                    value={filtration?.representative}
                    placeholder={"Account representative..."}
                />
              </Col>
            </Row>
            <div className="mb-3 text-center mt-4">
              <Button
                  color="primary"
                  onClick={() => loadResults(filtration)}
                  style={filtersChanged ? {
                    boxShadow: "0 0 16px 3px rgba(0, 123, 255, 0.7)",
                    animation: "show-results-pulse 1.5s ease-in-out infinite",
                  } : undefined}
              >
                Show results
              </Button>
            </div>
            {appliedFiltration === null ? (
              <CardBody className="text-center">
                <h5>Click &quot;Show results&quot; to load data</h5>
              </CardBody>
            ) : (
            <div className="tab default mt-3">
              <Nav tabs>
                <NavItem>
                  <NavLink
                      className={classnames({ active: activeTab === "summary" })}
                      onClick={() => {
                        setActiveTab("summary");
                        pushWithTab(filtration, "summary");
                      }}
                  >
                    <strong>Summary</strong>
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                      className={classnames({ active: activeTab === "byorder" })}
                      onClick={() => {
                        setActiveTab("byorder");
                        pushWithTab(filtration, "byorder");
                      }}
                  >
                    <strong>Detail by order</strong>
                  </NavLink>
                </NavItem>
              </Nav>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="summary" style={{ minHeight: "100%" }}>
                  <OrderSummaryByDate
                      data={summaryData}
                      dataDate={summaryDataDate}
                      filtration={appliedFiltration ?? filtration}
                  />
                </TabPane>
                <TabPane tabId="byorder" style={{ minHeight: "100%" }}>
                  <OrderSummaryByOrder
                      data={byOrderData}
                      filtration={appliedFiltration ?? filtration}
                  />
                </TabPane>
              </TabContent>
            </div>
            )}
          </>
        )}
      </Container>
  );
};

export default OrderSummary;
